import { useState } from 'react';
import { api, RequestFailed } from '../api/client';
import type { Person } from '../api/types';
import { PersonSearch } from './PersonSearch';

interface Issued {
  displayName: string;
  url: string;
  expiresAt: string;
}

/**
 * Выдача ссылки на смену пароля.
 *
 * Своего восстановления у участников нет — почты мы не собираем. Поэтому
 * администратор находит человека по имени и отдаёт ему одноразовую ссылку
 * любым удобным способом.
 */
export function PasswordResetPanel() {
  const [issued, setIssued] = useState<Issued | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function issue(person: Person) {
    setError(null);
    setBusy(true);
    setCopied(false);
    try {
      const link = await api.createPasswordReset(person.id);
      setIssued({ displayName: link.displayName, url: link.url, expiresAt: link.expiresAt });
    } catch (e) {
      setError(e instanceof RequestFailed ? e.error.message : 'Не удалось выдать ссылку');
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Копирование могло быть запрещено — ссылку всё равно видно и можно выделить
      setError('Скопировать не удалось, выделите ссылку вручную');
    }
  }

  return (
    <div className="panel-search">
      <div className="section-title" style={{ padding: '10px 0 2px' }}>
        Сменить пароль участнику
      </div>

      <PersonSearch
        onSearch={(query) => api.searchUsers(query)}
        onPick={(person) => void issue(person)}
        actionLabel={busy ? 'выдаём…' : 'выдать ссылку'}
        placeholder="полное имя или логин"
      />

      {issued && (
        <div className="reset-issued">
          <div className="name">Ссылка для: {issued.displayName}</div>
          {/* Показываем ссылку целиком: администратор передаёт её человеку сам,
              никаких писем мы не отправляем */}
          <div className="reset-link">{issued.url}</div>
          <div className="panel-actions">
            <button type="button" className="ghost" onClick={() => void copy(issued.url)}>
              {copied ? '✓ скопировано' : 'копировать'}
            </button>
            <span className="panel-hint">
              годна до {new Date(issued.expiresAt).toLocaleString('ru-RU', {
                day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>
          <p className="panel-hint">
            Ссылка одноразовая. После смены пароля человек выйдет из аккаунта
            на всех устройствах — это защита на случай, если доступом
            воспользовался кто-то ещё.
          </p>
        </div>
      )}

      {error && <p className="error">{error}</p>}
    </div>
  );
}
